import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Upload, Download, FileSpreadsheet, Users, Briefcase, Calendar,
  Folder, Settings, ArrowRight, CheckCircle2, AlertCircle, AlertTriangle,
  FileDown, FileUp, BarChart3, History, Play, Eye, Loader2, RefreshCw,
  ChevronRight, Info, XCircle, Clock
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const MODULE_CONFIG = {
  clients: { icon: Users, label: 'Clients', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  employees: { icon: Briefcase, label: 'Employees', color: 'text-green-600', bgColor: 'bg-green-50' },
  documents: { icon: Folder, label: 'Documents', color: 'text-purple-600', bgColor: 'bg-purple-50', disabled: true },
  schedules: { icon: Calendar, label: 'Schedules', color: 'text-orange-600', bgColor: 'bg-orange-50', disabled: true },
  agency_settings: { icon: Settings, label: 'Agency Settings', color: 'text-gray-600', bgColor: 'bg-gray-50', disabled: true },
};

// Health Score Ring Component
function HealthScoreRing({ score, size = 120, label }) {
  const radius = (size - 16) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;
  
  const getColor = (s) => {
    if (s >= 90) return '#22c55e';
    if (s >= 70) return '#eab308';
    if (s >= 50) return '#f97316';
    return '#ef4444';
  };
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={getColor(score)}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold">{score}%</span>
        </div>
      </div>
      {label && <span className="text-sm text-muted-foreground mt-2">{label}</span>}
    </div>
  );
}

// Mini Score Bar Component
function MiniScoreBar({ label, score }) {
  const getColor = (s) => {
    if (s >= 90) return 'bg-green-500';
    if (s >= 70) return 'bg-yellow-500';
    if (s >= 50) return 'bg-orange-500';
    return 'bg-red-500';
  };
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium">{score}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${getColor(score)} transition-all duration-500`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

export default function MigrationCenter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('import');
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('clients');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importJobs, setImportJobs] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [currentJob, setCurrentJob] = useState(null);

  useEffect(() => {
    loadModules();
    loadImportJobs();
  }, []);

  useEffect(() => {
    if (selectedModule) {
      loadTemplate(selectedModule);
    }
  }, [selectedModule]);

  const loadModules = async () => {
    try {
      const response = await api.get('/migration/modules');
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to load modules', error);
    }
  };

  const loadTemplate = async (moduleId) => {
    try {
      const response = await api.get(`/migration/templates/${moduleId}`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Failed to load template', error);
    }
  };

  const loadImportJobs = async () => {
    try {
      const response = await api.get('/migration/jobs?limit=10');
      setImportJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to load jobs', error);
    }
  };

  const handleDownloadTemplate = async (format) => {
    try {
      setLoading(true);
      const response = await api.get(`/migration/templates/${selectedModule}/download?format=${format}`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedModule}_import_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded ${selectedModule} template as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to download template');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async (format) => {
    try {
      setLoading(true);
      const response = await api.get(`/migration/export/${selectedModule}?format=${format}`, {
        responseType: 'blob',
      });
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `${selectedModule}_export.${format}`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Exported ${selectedModule} data as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    const allowedTypes = ['.csv', '.xlsx', '.xls', '.json'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error('Invalid file type. Please use CSV, Excel (.xlsx), or JSON files.');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/migration/upload/${selectedModule}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        setCurrentJob(response.data);
        toast.success(`File uploaded: ${response.data.total_rows} rows detected`);
        // Auto-analyze
        await handleAnalyze(response.data.job_id);
      } else {
        toast.error(response.data.errors?.[0] || 'Upload failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
      loadImportJobs();
    }
  };

  const handleAnalyze = async (jobId) => {
    try {
      setLoading(true);
      const response = await api.post(`/migration/jobs/${jobId}/analyze`);
      setAnalysisResult(response.data);
      setActiveTab('analyze');
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDryRun = async () => {
    if (!currentJob?.job_id) return;
    
    try {
      setLoading(true);
      // First validate
      await api.post(`/migration/jobs/${currentJob.job_id}/validate`);
      // Then dry run
      const response = await api.post(`/migration/jobs/${currentJob.job_id}/dry-run`);
      setDryRunResult(response.data);
      setActiveTab('dry-run');
    } catch (error) {
      toast.error('Dry run failed');
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = () => {
    navigate('/data-migration');
  };

  const ModuleIcon = MODULE_CONFIG[selectedModule]?.icon || FileSpreadsheet;

  return (
    <div className="px-4 md:px-8 py-6 md:py-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Migration Resource Center</h1>
          <p className="text-muted-foreground mt-1">
            Import, export, and manage your agency data
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadImportJobs}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleStartImport} data-testid="start-import-wizard">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* Module Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(MODULE_CONFIG).map(([key, config]) => {
              const Icon = config.icon;
              const isSelected = selectedModule === key;
              return (
                <button
                  key={key}
                  onClick={() => !config.disabled && setSelectedModule(key)}
                  disabled={config.disabled}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                    config.disabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`module-selector-${key}`}
                >
                  <Icon className={`w-4 h-4 ${isSelected ? 'text-primary' : config.color}`} />
                  <span className="font-medium">{config.label}</span>
                  {config.disabled && <Badge variant="secondary" className="text-xs">Soon</Badge>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="import" className="flex items-center gap-2">
            <Upload className="w-4 h-4" />
            <span className="hidden sm:inline">Import</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileDown className="w-4 h-4" />
            <span className="hidden sm:inline">Templates</span>
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2">
            <FileUp className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </TabsTrigger>
          <TabsTrigger value="analyze" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Analyze</span>
          </TabsTrigger>
          <TabsTrigger value="dry-run" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <span className="hidden sm:inline">Dry Run</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">History</span>
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upload Area */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ModuleIcon className={`w-5 h-5 ${MODULE_CONFIG[selectedModule]?.color}`} />
                  Import {MODULE_CONFIG[selectedModule]?.label}
                </CardTitle>
                <CardDescription>
                  Upload a CSV or Excel file to import {selectedModule}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => document.getElementById('file-upload-center').click()}
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary'); }}
                  onDragLeave={(e) => { e.currentTarget.classList.remove('border-primary'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary');
                    const file = e.dataTransfer.files[0];
                    if (file) handleFileUpload(file);
                  }}
                >
                  <input
                    id="file-upload-center"
                    type="file"
                    className="hidden"
                    accept=".csv,.xlsx,.xls,.json"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleFileUpload(file);
                    }}
                  />
                  {loading ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-12 h-12 text-primary animate-spin" />
                      <p className="text-muted-foreground">Processing file...</p>
                    </div>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-lg font-medium mb-2">
                        Drop your file here or click to browse
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Supports CSV, Excel (.xlsx), and JSON up to 10MB
                      </p>
                    </>
                  )}
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={() => handleDownloadTemplate('xlsx')}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleStartImport}>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Open Full Wizard
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Button variant="outline" className="h-auto py-4 flex flex-col" onClick={() => setActiveTab('templates')}>
                    <FileDown className="w-6 h-6 mb-2" />
                    <span className="font-medium">Download Template</span>
                    <span className="text-xs text-muted-foreground">CSV or Excel</span>
                  </Button>
                  <Button variant="outline" className="h-auto py-4 flex flex-col" onClick={() => setActiveTab('export')}>
                    <FileUp className="w-6 h-6 mb-2" />
                    <span className="font-medium">Export Data</span>
                    <span className="text-xs text-muted-foreground">Backup or edit</span>
                  </Button>
                </div>
                
                {currentJob && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium">File Ready</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                      {currentJob.total_rows} records detected from {currentJob.file_name || 'uploaded file'}
                    </p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleAnalyze(currentJob.job_id)}>
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Analyze
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleDryRun}>
                        <Eye className="w-4 h-4 mr-2" />
                        Dry Run
                      </Button>
                      <Button size="sm" onClick={handleStartImport}>
                        <Play className="w-4 h-4 mr-2" />
                        Import
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileDown className="w-5 h-5" />
                Download Import Templates
              </CardTitle>
              <CardDescription>
                Get pre-formatted templates with all fields, sample data, and instructions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <FileSpreadsheet className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">Excel Template (.xlsx)</h4>
                      <p className="text-sm text-muted-foreground">Includes instructions sheet</p>
                    </div>
                  </div>
                  <Button onClick={() => handleDownloadTemplate('xlsx')} disabled={loading} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download Excel
                  </Button>
                </div>
                
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-medium">CSV Template (.csv)</h4>
                      <p className="text-sm text-muted-foreground">Simple comma-separated format</p>
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => handleDownloadTemplate('csv')} disabled={loading} className="w-full">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download CSV
                  </Button>
                </div>
              </div>

              {/* Field Reference */}
              {template && (
                <div>
                  <h4 className="font-medium mb-3">Field Reference</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/4">Field Name</TableHead>
                          <TableHead className="w-1/6">Required</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-1/4">Example</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {template.fields?.slice(0, 10).map((field) => (
                          <TableRow key={field.name}>
                            <TableCell className="font-mono text-sm">{field.name}</TableCell>
                            <TableCell>
                              {field.required ? (
                                <Badge variant="destructive" className="text-xs">Required</Badge>
                              ) : (
                                <Badge variant="secondary" className="text-xs">Optional</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{field.description}</TableCell>
                            <TableCell className="text-sm font-mono">{field.example}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {template.fields?.length > 10 && (
                      <div className="p-3 bg-muted/50 text-center text-sm text-muted-foreground">
                        + {template.fields.length - 10} more fields (download template for complete list)
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Formatting Notes */}
              {template?.formatting_notes && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Formatting Notes
                  </h4>
                  <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
                    {template.formatting_notes.map((note, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {note}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileUp className="w-5 h-5" />
                Export Existing Data
              </CardTitle>
              <CardDescription>
                Download your current {selectedModule} data for backup, editing, or audit purposes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-1">Backup</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Create a copy of your data before making changes
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-1">Bulk Edit</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Export, modify in Excel, then re-import
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium mb-1">Audit Copy</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Keep records for compliance and reporting
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => handleExportData('xlsx')} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export as Excel
                </Button>
                <Button variant="outline" onClick={() => handleExportData('csv')} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Export as CSV
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analyze Tab */}
        <TabsContent value="analyze" className="space-y-6">
          {analysisResult ? (
            <>
              {/* Migration Readiness Score */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Migration Readiness
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="flex justify-center">
                      <HealthScoreRing 
                        score={analysisResult.health_scores?.overall || 0} 
                        size={140}
                        label="Overall"
                      />
                    </div>
                    <div className="space-y-4 lg:col-span-3">
                      <MiniScoreBar 
                        label={MODULE_CONFIG[selectedModule]?.label || 'Records'} 
                        score={analysisResult.health_scores?.[selectedModule] || 0} 
                      />
                      <MiniScoreBar 
                        label="Required Fields" 
                        score={analysisResult.health_scores?.required_fields || 0} 
                      />
                      <MiniScoreBar 
                        label="Duplicates" 
                        score={analysisResult.health_scores?.duplicates || 0} 
                      />
                      <MiniScoreBar 
                        label="Data Quality" 
                        score={analysisResult.health_scores?.data_quality || 0} 
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recommendation */}
              {analysisResult.recommendation && (
                <Card className={`border-l-4 ${
                  analysisResult.recommendation.color === 'green' ? 'border-l-green-500 bg-green-50/50' :
                  analysisResult.recommendation.color === 'yellow' ? 'border-l-yellow-500 bg-yellow-50/50' :
                  analysisResult.recommendation.color === 'orange' ? 'border-l-orange-500 bg-orange-50/50' :
                  'border-l-red-500 bg-red-50/50'
                }`}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      {analysisResult.recommendation.color === 'green' ? (
                        <CheckCircle2 className="w-8 h-8 text-green-600" />
                      ) : analysisResult.recommendation.color === 'yellow' ? (
                        <AlertTriangle className="w-8 h-8 text-yellow-600" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-600" />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold">{analysisResult.recommendation.title}</h3>
                        <p className="text-muted-foreground mt-1">{analysisResult.recommendation.message}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold">{analysisResult.total_records}</div>
                    <div className="text-sm text-muted-foreground">Total Records</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-green-600">{analysisResult.valid_records}</div>
                    <div className="text-sm text-muted-foreground">Valid</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-red-600">{analysisResult.invalid_records}</div>
                    <div className="text-sm text-muted-foreground">Invalid</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-3xl font-bold text-yellow-600">{analysisResult.duplicate_candidates}</div>
                    <div className="text-sm text-muted-foreground">Duplicates</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{analysisResult.estimated_import_time}</div>
                        <div className="text-xs text-muted-foreground">Est. Import Time</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Issues */}
              {Object.entries(analysisResult.issues || {}).some(([_, items]) => items?.length > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Issues Found</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analysisResult.issues?.missing_required?.length > 0 && (
                        <div className="p-3 bg-red-50 rounded-lg">
                          <h4 className="font-medium text-red-700 flex items-center gap-2">
                            <XCircle className="w-4 h-4" />
                            Missing Required Fields ({analysisResult.issues.missing_required.length})
                          </h4>
                          <ul className="mt-2 text-sm text-red-600 space-y-1">
                            {analysisResult.issues.missing_required.slice(0, 5).map((issue, idx) => (
                              <li key={idx}>Row {issue.row}: {issue.message}</li>
                            ))}
                            {analysisResult.issues.missing_required.length > 5 && (
                              <li>...and {analysisResult.issues.missing_required.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {analysisResult.issues?.invalid_dates?.length > 0 && (
                        <div className="p-3 bg-yellow-50 rounded-lg">
                          <h4 className="font-medium text-yellow-700 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Invalid Dates ({analysisResult.issues.invalid_dates.length})
                          </h4>
                          <ul className="mt-2 text-sm text-yellow-600 space-y-1">
                            {analysisResult.issues.invalid_dates.slice(0, 3).map((issue, idx) => (
                              <li key={idx}>Row {issue.row}: {issue.message}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {analysisResult.issues?.invalid_emails?.length > 0 && (
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <h4 className="font-medium text-orange-700 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Invalid Emails ({analysisResult.issues.invalid_emails.length})
                          </h4>
                        </div>
                      )}
                      {analysisResult.issues?.invalid_phones?.length > 0 && (
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <h4 className="font-medium text-blue-700 flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            Incomplete Phones ({analysisResult.issues.invalid_phones.length})
                          </h4>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={handleDryRun} variant="outline">
                  <Eye className="w-4 h-4 mr-2" />
                  Run Dry Run Preview
                </Button>
                <Button onClick={handleStartImport}>
                  <Play className="w-4 h-4 mr-2" />
                  Proceed to Import
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Analysis Available</h3>
                <p className="text-muted-foreground mb-4">
                  Upload a file first to see the pre-import analysis
                </p>
                <Button variant="outline" onClick={() => setActiveTab('import')}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Dry Run Tab */}
        <TabsContent value="dry-run" className="space-y-6">
          {dryRunResult ? (
            <>
              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Dry Run Preview
                  </CardTitle>
                  <CardDescription>
                    This preview shows exactly what would happen if you proceed with the import. No data has been written.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <div className="text-2xl font-bold">{dryRunResult.summary?.total || 0}</div>
                      <div className="text-sm text-muted-foreground">Total Records</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{dryRunResult.summary?.create_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Will Create</div>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{dryRunResult.summary?.merge_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Will Merge</div>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-yellow-600">{dryRunResult.summary?.skip_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Will Skip</div>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-600">{dryRunResult.summary?.error_count || 0}</div>
                      <div className="text-sm text-muted-foreground">Errors</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Records to Create */}
              {dryRunResult.would_create?.length > 0 && (
                <Card>
                  <CardHeader className="bg-green-50/50">
                    <CardTitle className="text-green-700 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Records to Create ({dryRunResult.would_create.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {dryRunResult.would_create.slice(0, 10).map((record, idx) => (
                        <div key={idx} className="p-3 bg-green-50 rounded-lg flex items-center gap-3">
                          <Badge variant="outline">Row {record.row}</Badge>
                          <span className="font-medium">
                            {record.data?.first_name} {record.data?.last_name}
                          </span>
                          {record.data?.email && (
                            <span className="text-sm text-muted-foreground">{record.data.email}</span>
                          )}
                        </div>
                      ))}
                      {dryRunResult.would_create.length > 10 && (
                        <div className="text-sm text-muted-foreground text-center py-2">
                          +{dryRunResult.would_create.length - 10} more records
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Records to Skip */}
              {dryRunResult.would_skip?.length > 0 && (
                <Card>
                  <CardHeader className="bg-yellow-50/50">
                    <CardTitle className="text-yellow-700 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Records to Skip ({dryRunResult.would_skip.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {dryRunResult.would_skip.map((record, idx) => (
                        <div key={idx} className="p-3 bg-yellow-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline">Row {record.row}</Badge>
                            <span className="font-medium">
                              {record.data?.first_name} {record.data?.last_name}
                            </span>
                          </div>
                          <div className="text-sm text-yellow-700 mt-1">{record.reason}</div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Validation Errors */}
              {dryRunResult.validation_errors?.length > 0 && (
                <Card>
                  <CardHeader className="bg-red-50/50">
                    <CardTitle className="text-red-700 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Validation Errors ({dryRunResult.validation_errors.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {dryRunResult.validation_errors.map((error, idx) => (
                        <div key={idx} className="p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Badge variant="destructive">Row {error.row}</Badge>
                          </div>
                          <ul className="text-sm text-red-700 mt-1 space-y-1">
                            {error.errors?.map((e, i) => (
                              <li key={i}>{e.field}: {e.message}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setActiveTab('analyze')}>
                  <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                  Back to Analysis
                </Button>
                <Button onClick={handleStartImport}>
                  <Play className="w-4 h-4 mr-2" />
                  Proceed to Import
                </Button>
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Dry Run Available</h3>
                <p className="text-muted-foreground mb-4">
                  Upload and analyze a file first, then run a dry run preview
                </p>
                <Button variant="outline" onClick={() => setActiveTab('import')}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Import History
              </CardTitle>
              <CardDescription>
                View previous imports, their status, and rollback options
              </CardDescription>
            </CardHeader>
            <CardContent>
              {importJobs.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Module</TableHead>
                      <TableHead>File</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="text-sm">
                          {new Date(job.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{job.module}</Badge>
                        </TableCell>
                        <TableCell className="text-sm">{job.file_name}</TableCell>
                        <TableCell>{job.total_rows}</TableCell>
                        <TableCell>
                          <Badge variant={
                            job.status === 'completed' ? 'default' :
                            job.status === 'failed' ? 'destructive' :
                            'secondary'
                          }>
                            {job.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No import history yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
